import { Link } from "react-router-dom";
import type { Article } from "@/mocks/articles";
import { getTopicName } from "@/mocks/topics";

type Props = {
  article: Article;
  variant?: "default" | "wide" | "compact";
  rank?: number;
};

export default function ArticleCard({ article, variant = "default", rank }: Props) {
  if (variant === "compact") {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex items-start gap-4 border-b border-background-200 py-4 last:border-b-0"
      >
        {typeof rank === "number" && (
          <span className="mt-0.5 font-heading text-xl font-bold text-accent-600 w-7 shrink-0">
            {String(rank).padStart(2, "0")}
          </span>
        )}
        <span className="flex flex-col">
          <span className="font-heading text-sm font-semibold leading-relaxed text-foreground-900 transition-colors group-hover:text-primary-600 md:text-base">
            {article.title}
          </span>
          <span className="mt-2 flex items-center gap-3 text-xs text-foreground-500">
            <span>{getTopicName(article.category)}</span>
            <span aria-hidden="true">·</span>
            <span>{article.date}</span>
          </span>
        </span>
      </Link>
    );
  }

  const isWide = variant === "wide";

  return (
    <article
      className={[
        "group overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors duration-200 hover:border-primary-300",
        isWide ? "md:flex" : "flex flex-col",
      ].join(" ")}
    >
      <Link
        to={`/article/${article.id}`}
        className={[
          "block shrink-0 overflow-hidden",
          isWide ? "md:w-2/5" : "",
        ].join(" ")}
        aria-label={article.title}
      >
        <div className={isWide ? "h-52 w-full md:h-full md:min-h-[240px]" : "h-48 w-full"}>
          <img
            src={article.cover}
            alt={`${article.title} - 选美吃瓜深度解析配图`}
            title={`${article.title} - 选美吃瓜`}
            className="h-full w-full rounded-t-lg object-cover object-top transition-transform duration-500 group-hover:scale-105 md:rounded-t-none md:rounded-l-lg"
          />
        </div>
      </Link>

      <div className="flex flex-1 flex-col p-5 md:p-6">
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded-full bg-secondary-100 px-3 py-1 font-label text-xs text-secondary-900">
            {getTopicName(article.category)}
          </span>
          <span className="flex items-center gap-1 text-xs text-foreground-500">
            <i className="ri-fire-line text-primary-500" aria-hidden="true"></i>
            热度 {article.heat}
          </span>
        </div>

        <h3 className="mt-3 font-heading text-lg font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600">
          <Link to={`/article/${article.id}`}>{article.title}</Link>
        </h3>

        <p className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">
          {article.excerpt}
        </p>

        <div className="mt-5 flex items-center justify-between text-xs text-foreground-500">
          <span className="flex items-center gap-3">
            <span>{article.author}</span>
            <span aria-hidden="true">·</span>
            <span>{article.readMinutes} 分钟</span>
          </span>
          <Link
            to={`/article/${article.id}`}
            className="flex items-center gap-1 font-label text-primary-600 hover:text-primary-700"
          >
            阅读
            <i className="ri-arrow-right-line" aria-hidden="true"></i>
          </Link>
        </div>
      </div>
    </article>
  );
}